import piexif from 'piexifjs';
import {
  buildTextField,
  buildTextareaField,
  buildSelectField,
  buildGpsCoordinateField,
  buildFractionNumberField,
  buildNumberField,
  buildDateField,
  buildShutterSpeedField,
  buildAltitudeField
} from './builders/index.js';

const FILM_STOCK_EXIF_TAG = 0xA436;

// piexifjs does not expose ExifIFD.ImageTitle, but it can serialize the tag once
// the internal registry knows its type.
if (!piexif.ExifIFD.ImageTitle) {
  piexif.ExifIFD.ImageTitle = FILM_STOCK_EXIF_TAG;
}
if (!piexif.TAGS.Exif[FILM_STOCK_EXIF_TAG]) {
  piexif.TAGS.Exif[FILM_STOCK_EXIF_TAG] = {
    name: 'ImageTitle',
    type: 'Ascii'
  };
}

// Field Types
export const FIELD_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'datetime-local',
  GPS_COORDINATE: 'gps-coordinate',
  SELECT: 'select',
  TEXTAREA: 'textarea'
};

// Field Categories
export const FIELD_CATEGORIES = {
  DATE_TIME: 'date-time',
  LOCATION: 'location',
  CAMERA: 'camera',
  LENS: 'lens',
  IMAGE_SETTINGS: 'image-settings',
  DESCRIPTIVE: 'descriptive',
  COPYRIGHT: 'copyright'
};

// Complete EXIF field definitions using builders for DRY, SOLID code
export const EXIF_FIELDS = {
  // ==================== Date & Time Category ====================
  dateTaken: buildDateField({
    id: 'dateTaken',
    label: 'Date Taken',
    category: FIELD_CATEGORIES.DATE_TIME,
    exifPath: 'Exif',
    exifTag: piexif.ExifIFD.DateTimeOriginal,
    exifSecondaryTags: [
      { path: 'Exif', tag: piexif.ExifIFD.DateTimeDigitized },
      { path: '0th', tag: piexif.ImageIFD.DateTime }
    ]
  }),

  // ==================== Location Category ====================
  latitude: buildGpsCoordinateField({
    id: 'latitude',
    label: 'Latitude',
    category: FIELD_CATEGORIES.LOCATION,
    exifTag: piexif.GPSIFD.GPSLatitude,
    exifRefTag: { tag: piexif.GPSIFD.GPSLatitudeRef, positiveValue: 'N', negativeValue: 'S' }
  }),

  longitude: buildGpsCoordinateField({
    id: 'longitude',
    label: 'Longitude',
    category: FIELD_CATEGORIES.LOCATION,
    exifTag: piexif.GPSIFD.GPSLongitude,
    exifRefTag: { tag: piexif.GPSIFD.GPSLongitudeRef, positiveValue: 'E', negativeValue: 'W' }
  }),

  altitude: buildAltitudeField({
    id: 'altitude',
    label: 'Altitude (m)',
    category: FIELD_CATEGORIES.LOCATION,
    exifTag: piexif.GPSIFD.GPSAltitude,
    exifRefTag: { tag: piexif.GPSIFD.GPSAltitudeRef, positiveValue: 0, negativeValue: 1 }
  }),

  // ==================== Camera Category ====================
  cameraMake: buildTextField({
    id: 'cameraMake',
    label: 'Camera Make',
    category: FIELD_CATEGORIES.CAMERA,
    exifPath: '0th',
    exifTag: piexif.ImageIFD.Make,
    options: { presetGroup: 'camera', displayGroup: 'camera' }
  }),

  cameraModel: buildTextField({
    id: 'cameraModel',
    label: 'Camera Model',
    category: FIELD_CATEGORIES.CAMERA,
    exifPath: '0th',
    exifTag: piexif.ImageIFD.Model,
    options: { presetGroup: 'camera', displayGroup: 'camera' }
  }),

  // ==================== Lens Category ====================
  lensModel: buildTextField({
    id: 'lensModel',
    label: 'Lens Model',
    category: FIELD_CATEGORIES.LENS,
    exifPath: 'Exif',
    exifTag: piexif.ExifIFD.LensModel,
    options: { presetGroup: 'lens', displayGroup: 'lens' }
  }),

  lensMake: buildTextField({
    id: 'lensMake',
    label: 'Lens Make',
    category: FIELD_CATEGORIES.LENS,
    exifPath: 'Exif',
    exifTag: piexif.ExifIFD.LensMake,
    options: { presetGroup: 'lens', displayGroup: 'lens' }
  }),

  // ==================== Image Settings Category ====================
  iso: buildNumberField({
    id: 'iso',
    label: 'ISO',
    category: FIELD_CATEGORIES.IMAGE_SETTINGS,
    exifPath: 'Exif',
    exifTag: piexif.ExifIFD.ISOSpeedRatings,
    displayOptions: { prefix: 'ISO ', formatFn: (v) => v }
  }),

  aperture: (function() {
    const field = buildFractionNumberField({
      id: 'aperture',
      label: 'Aperture',
      category: FIELD_CATEGORIES.IMAGE_SETTINGS,
      exifPath: 'Exif',
      exifTag: piexif.ExifIFD.FNumber,
      multiplier: 10
    });
    // Override display for f/ prefix
    field.formatForDisplay = (value) => value != null ? `f/${value}` : '-';
    return field;
  })(),

  shutterSpeed: buildShutterSpeedField({
    id: 'shutterSpeed',
    label: 'Shutter Speed',
    category: FIELD_CATEGORIES.IMAGE_SETTINGS,
    exifPath: 'Exif',
    exifTag: piexif.ExifIFD.ExposureTime
  }),

  focalLength: buildFractionNumberField({
    id: 'focalLength',
    label: 'Focal Length',
    category: FIELD_CATEGORIES.IMAGE_SETTINGS,
    exifPath: 'Exif',
    exifTag: piexif.ExifIFD.FocalLength,
    multiplier: 10,
    suffix: 'mm'
  }),

  whiteBalance: buildSelectField({
    id: 'whiteBalance',
    label: 'White Balance',
    category: FIELD_CATEGORIES.IMAGE_SETTINGS,
    exifPath: 'Exif',
    exifTag: piexif.ExifIFD.WhiteBalance,
    options: [
      { value: 0, label: 'Auto' },
      { value: 1, label: 'Manual' }
    ]
  }),

  flash: buildSelectField({
    id: 'flash',
    label: 'Flash',
    category: FIELD_CATEGORIES.IMAGE_SETTINGS,
    exifPath: 'Exif',
    exifTag: piexif.ExifIFD.Flash,
    options: [
      { value: 0, label: 'No Flash' },
      { value: 1, label: 'Flash Fired' },
      { value: 5, label: 'Strobe Return Light Not Detected' },
      { value: 7, label: 'Strobe Return Light Detected' },
      { value: 9, label: 'Flash Fired, Compulsory' },
      { value: 16, label: 'Flash Did Not Fire, Compulsory' },
      { value: 24, label: 'Flash Did Not Fire, Auto' },
      { value: 25, label: 'Flash Fired, Auto' }
    ]
  }),

  orientation: buildSelectField({
    id: 'orientation',
    label: 'Orientation',
    category: FIELD_CATEGORIES.IMAGE_SETTINGS,
    exifPath: '0th',
    exifTag: piexif.ImageIFD.Orientation,
    options: [
      { value: 1, label: 'Horizontal (normal)' },
      { value: 2, label: 'Mirror horizontal' },
      { value: 3, label: 'Rotate 180' },
      { value: 4, label: 'Mirror vertical' },
      { value: 5, label: 'Mirror horizontal and rotate 270 CW' },
      { value: 6, label: 'Rotate 90 CW' },
      { value: 7, label: 'Mirror horizontal and rotate 90 CW' },
      { value: 8, label: 'Rotate 270 CW' }
    ],
    formatterOptions: { defaultValue: 1, fallbackValue: 1 }
  }),

  // ==================== Descriptive Category ====================
  description: buildTextareaField({
    id: 'description',
    label: 'Description',
    category: FIELD_CATEGORIES.DESCRIPTIVE,
    exifPath: '0th',
    exifTag: piexif.ImageIFD.ImageDescription
  }),

  filmStock: buildTextField({
    id: 'filmStock',
    label: 'Film Stock',
    category: FIELD_CATEGORIES.DESCRIPTIVE,
    exifPath: 'Exif',
    exifTag: piexif.ExifIFD.ImageTitle
  }),

  userComment: buildTextareaField({
    id: 'userComment',
    label: 'User Comment',
    category: FIELD_CATEGORIES.DESCRIPTIVE,
    exifPath: 'Exif',
    exifTag: piexif.ExifIFD.UserComment,
    options: { specialFromExif: true }
  }),

  // ==================== Copyright Category ====================
  artist: buildTextField({
    id: 'artist',
    label: 'Artist',
    category: FIELD_CATEGORIES.COPYRIGHT,
    exifPath: '0th',
    exifTag: piexif.ImageIFD.Artist
  }),

  copyright: buildTextField({
    id: 'copyright',
    label: 'Copyright',
    category: FIELD_CATEGORIES.COPYRIGHT,
    exifPath: '0th',
    exifTag: piexif.ImageIFD.Copyright
  }),

  software: buildTextField({
    id: 'software',
    label: 'Software',
    category: FIELD_CATEGORIES.COPYRIGHT,
    exifPath: '0th',
    exifTag: piexif.ImageIFD.Software,
    options: { defaultValue: 'Exiftrator' }
  })
};

// Software must always be written with our app identifier
EXIF_FIELDS.software.formatForExif = () => 'Exiftrator';

// Category metadata with icons and descriptions
export const CATEGORY_METADATA = {
  [FIELD_CATEGORIES.DATE_TIME]: {
    id: FIELD_CATEGORIES.DATE_TIME,
    label: 'Date & Time',
    icon: 'Calendar',
    description: 'When the photo was taken'
  },
  [FIELD_CATEGORIES.LOCATION]: {
    id: FIELD_CATEGORIES.LOCATION,
    label: 'Location',
    icon: 'MapPin',
    description: 'GPS coordinates and location data'
  },
  [FIELD_CATEGORIES.CAMERA]: {
    id: FIELD_CATEGORIES.CAMERA,
    label: 'Camera',
    icon: 'Camera',
    description: 'Camera body information'
  },
  [FIELD_CATEGORIES.LENS]: {
    id: FIELD_CATEGORIES.LENS,
    label: 'Lens',
    icon: 'Search',
    description: 'Lens information'
  },
  [FIELD_CATEGORIES.IMAGE_SETTINGS]: {
    id: FIELD_CATEGORIES.IMAGE_SETTINGS,
    label: 'Image Settings',
    icon: 'Settings',
    description: 'Exposure, ISO, and other settings'
  },
  [FIELD_CATEGORIES.DESCRIPTIVE]: {
    id: FIELD_CATEGORIES.DESCRIPTIVE,
    label: 'Descriptive',
    icon: 'FileText',
    description: 'Descriptions and comments'
  },
  [FIELD_CATEGORIES.COPYRIGHT]: {
    id: FIELD_CATEGORIES.COPYRIGHT,
    label: 'Copyright',
    icon: 'Copyright',
    description: 'Copyright and attribution'
  }
};

// Default category order
export const DEFAULT_CATEGORY_ORDER = [
  FIELD_CATEGORIES.DATE_TIME,
  FIELD_CATEGORIES.LOCATION,
  FIELD_CATEGORIES.CAMERA,
  FIELD_CATEGORIES.LENS,
  FIELD_CATEGORIES.IMAGE_SETTINGS,
  FIELD_CATEGORIES.DESCRIPTIVE,
  FIELD_CATEGORIES.COPYRIGHT
];

// Helper function to get fields by category
export function getFieldsByCategory(categoryId, includeAll = true) {
  return Object.values(EXIF_FIELDS).filter(field => field.category === categoryId);
}

// Helper function to get enabled fields (from config)
export function getEnabledFields(config) {
  return Object.values(EXIF_FIELDS).filter(field => config.enabledFields[field.id]);
}
